import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const TrafficQualityChart = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [touchPoint, setTouchPoint] = useState('last');
  const [groupBy, setGroupBy] = useState('source'); // 'source' or 'location'
  const [trafficQualityData, setTrafficQualityData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Format source name (same as SourceChart)
  const formatSourceName = (source) => {
    if (!source || source === 'Direct') {
      return 'Direct';
    }
    try {
      let cleaned = source;
      
      // Handle android-app format first: android-app://com.linkedin.android -> linkedin.android
      if (source.includes('android-app://')) {
        cleaned = source.replace(/^android-app:\/\/?\/?/i, '');
        const parts = cleaned.split('/')[0].split('.');
        if (parts.length >= 3) {
          // Take last two parts: com.linkedin.android -> linkedin.android
          cleaned = parts.slice(-2).join('.');
        } else {
          cleaned = cleaned.split('/')[0];
        }
      } else {
        // Remove protocol (http://, https://, etc.)
        cleaned = cleaned.replace(/^[a-z]+:\/\/?\/?/i, '');
        
        // Remove path and query parameters (everything after first /)
        cleaned = cleaned.split('/')[0];
        
        // Remove port if present (e.g., example.com:8080)
        cleaned = cleaned.split(':')[0];
        
        // Remove www. prefix if present
        cleaned = cleaned.replace(/^www\./i, '');
        
        // Handle subdomains: if more than 2 parts, remove first part if it's short (m, api, app, etc.)
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          const firstPart = parts[0];
          // If first part is very short (1-2 chars) or common prefixes, remove it
          if (firstPart.length <= 1 || (firstPart.length === 2 && ['m', 'api', 'app'].includes(firstPart.toLowerCase()))) {
            cleaned = parts.slice(1).join('.');
          }
        }
      }
      
      // Capitalize first letter
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    } catch (error) {
      return source;
    }
  };

  useEffect(() => {
    const fetchTrafficQualityData = async () => {
      if (!teamId) return;

      setIsLoading(true);
      try {
        const websiteIds = appliedFilters?.websites?.length > 0 
          ? appliedFilters.websites 
          : 'all';

        const params = {
          team_id: teamId,
          websites: websiteIds === 'all' ? 'all' : JSON.stringify(websiteIds),
          touchPoint,
          groupBy,
          ...(appliedFilters?.dateRange?.from && { from: appliedFilters.dateRange.from }),
          ...(appliedFilters?.dateRange?.to && { to: appliedFilters.dateRange.to })
        };

        const response = await axiosInstance.get('/overview/traffic-quality-metrics', { params });

        if (response.data?.success) {
          const transformedData = response.data.trafficQualityData.map(item => ({
            source: item.source,
            formattedSource: groupBy === 'location' ? item.source : formatSourceName(item.source),
            avgTimeSpent: item.avgTimeSpent || 0,
            avgTransactions: item.avgTransactions || 0,
            userCount: item.userCount || 0
          }));

          setTrafficQualityData(transformedData);
        } else {
          setTrafficQualityData([]);
        }
      } catch (error) {
        console.error('Error fetching traffic quality data:', error);
        setTrafficQualityData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrafficQualityData();
  }, [teamId, touchPoint, groupBy, appliedFilters]);

  // Touch Point Toggle Component (same style as SourceChart)
  const TouchPointToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('first')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'first' ? styles.accentColor : styles.primaryBg,
          color: value === 'first' ? '#ffffff' : styles.textPrimary
        }}
      >
        First Touch Point
      </button>
      <button
        onClick={() => onChange('last')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'last' ? styles.accentColor : styles.primaryBg,
          color: value === 'last' ? '#ffffff' : styles.textPrimary
        }}
      >
        Last Touch Point
      </button>
    </div>
  );

  // Group By Toggle Component (Sources/Location)
  const GroupByToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('source')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'source' ? styles.accentColor : styles.primaryBg,
          color: value === 'source' ? '#ffffff' : styles.textPrimary
        }}
      >
        Sources
      </button>
      <button
        onClick={() => onChange('location')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'location' ? styles.accentColor : styles.primaryBg,
          color: value === 'location' ? '#ffffff' : styles.textPrimary
        }}
      >
        Location
      </button>
    </div>
  );

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border shadow-lg" style={{ borderRadius: '4px', borderColor: styles.borderColor }}>
          <p className="font-medium text-[#0f172a] mb-2" style={{ fontFamily: 'Raleway, sans-serif' }}>
            {data.formattedSource}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Avg Time Spent: {data.avgTimeSpent?.toFixed(2) || 0}s
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Avg Transactions: {data.avgTransactions?.toFixed(2) || 0}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm">
            Users: {data.userCount?.toLocaleString() || 0}
          </p>
        </div>
      );
    }
    return null;
  };

  // Colors for scatter points
  const scatterColors = [
    styles.accentColor,
    '#2a1a5a',
    '#3d2a6e',
    '#4a2f7a',
    '#5a3a8a',
    '#6a4a9a',
    '#7a5aaa'
  ];

  const loading = parentLoading || isLoading;

  return (
    <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
          Traffic Quality Analysis
        </h3>
        <div className="flex items-center gap-2">
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
          <TouchPointToggle value={touchPoint} onChange={setTouchPoint} />
        </div>
      </div>

      <div className="h-96">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Loading traffic quality data...
            </div>
          </div>
        ) : trafficQualityData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              No traffic quality data available
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={styles.borderColor} vertical={false} horizontal={true} />
              <XAxis 
                type="number"
                dataKey="avgTimeSpent"
                name="Avg Time Spent"
                label={{ 
                  value: 'Avg Time Spent (seconds)', 
                  position: 'insideBottom', 
                  offset: -10,
                  style: { textAnchor: 'middle', fontSize: '12px', fill: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }
                }}
                tick={{ 
                  fill: styles.textPrimary, 
                  fontSize: 11,
                  fontFamily: 'Raleway, sans-serif'
                }}
                axisLine={{ stroke: styles.borderColor }}
                tickFormatter={(value) => value.toFixed(0)}
                domain={[0, 'dataMax * 1.1']}
              />
              <YAxis 
                type="number"
                dataKey="avgTransactions"
                name="Avg Transactions"
                label={{ 
                  value: 'Avg No of Transactions', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: '12px', fill: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }
                }}
                tick={{ 
                  fill: styles.textPrimary, 
                  fontSize: 11,
                  fontFamily: 'Raleway, sans-serif'
                }}
                axisLine={{ stroke: styles.borderColor }}
                tickFormatter={(value) => value.toFixed(1)}
                domain={[0, 'dataMax * 1.1']}
              />
              <Tooltip content={<CustomTooltip />} />
              {trafficQualityData.map((item, index) => (
                <Scatter
                  key={item.source}
                  name={item.formattedSource}
                  data={[item]}
                  fill={scatterColors[index % scatterColors.length]}
                  shape="circle"
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default TrafficQualityChart;

